import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from './services/authentication.service';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { isPublicRoute } from './utils/public-route';
import { SKIP_AUTH_REDIRECT } from './skip-auth-redirect';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  constructor(
    private authenticationService: AuthenticationService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const user = this.authenticationService.currentUserValue;
    const token = this.authenticationService.currentTokenValue;
    const isAuthenticated = user && token;
    const isApiUrl = req.url.startsWith(environment.api.host);

    // Get local and UTC time using moment
    const localTime = moment().format('YYYY-MM-DD HH:mm:ss');
    const utcTime = moment().utc().format('YYYY-MM-DD HH:mm:ss');

    let headers: any = {
      'x-client-local-time': localTime,
      'x-client-utc-time': utcTime,
    };

    if (isAuthenticated && isApiUrl) {
      headers['authorization'] = 'Bearer ' + token;
    }

    req = req.clone({ setHeaders: headers });

    return next.handle(req).pipe(
      tap(
        () => {},
        (err: any) => {
          if (err instanceof HttpErrorResponse) {
            if (err.status !== 401) {
              return;
            }

            // Always clear the dead credentials, but only bounce to Login from
            // a page that needs a session. AppComponent probes
            // /api/user/is-authenticated on every boot, and since the server
            // began answering 401 (was 500) for a missing token that probe
            // logs out signed-out visitors sitting on a PUBLIC page - stomping
            // the invoice checkout/receipt URLs we email to families.
            //
            // Mid-boot the 401 can land before the first navigation commits, so
            // router.url is still '' - read the in-flight navigation's target
            // when there is one, and fall back to router.url once settled.
            this.authenticationService.deauthoriseUser(!this.shouldSuppressRedirect(req));
          }
        }
      )
    );
  }

  private shouldSuppressRedirect(req: HttpRequest<any>): boolean {
    if (req.context.get(SKIP_AUTH_REDIRECT)) {
      return true;
    }

    if (
      req.method === 'GET' &&
      req.url === environment.api.host + environment.api.paths.api.user.isAuthenticated
    ) {
      return true;
    }

    const navigation = this.router.getCurrentNavigation();
    const url = navigation ? navigation.extractedUrl.toString() : this.router.url;

    return isPublicRoute(url);
  }
}
